import { useCallback, useMemo } from 'react';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';

// Use a completely separate preference key for dev panel sections to avoid conflicts
export const DEV_INFO_PANEL_PREF_KEY_PREFIX = 'jira.user.dev-panel-collapse-state.';

export const getDevInfoPanelPrefKey = (projectKey: string) =>
	`${DEV_INFO_PANEL_PREF_KEY_PREFIX}${projectKey}`;

export type DevPanelSectionId = 'DEV_PANEL' | 'RELEASES_PANEL';

// Use the same structure as the working collapsible sections
export type BackendDevPanelState = {
	sections?: BackendDevPanelSectionState[];
};

export type BackendDevPanelSectionState = {
	section: DevPanelSectionId;
	isCollapsed: boolean | undefined;
};

// Type guard to check if the parsed object has the expected structure
function isBackendDevPanelState(obj: unknown): obj is BackendDevPanelState {
	if (obj === null || typeof obj !== 'object') {
		return false;
	}

	// Check if the object has a sections property and it's an array
	return 'sections' in obj && Array.isArray(obj.sections);
}

function updateBackendState(
	currentState: BackendDevPanelState | undefined,
	sectionId: DevPanelSectionId,
	newIsOpen: boolean,
	setValue: (val: string) => void,
) {
	const sections = currentState?.sections || [];
	const existingSectionIndex = sections.findIndex((s) => s.section === sectionId);

	let updatedSections: BackendDevPanelSectionState[];
	if (existingSectionIndex >= 0) {
		// Update existing section
		updatedSections = sections.map((s) =>
			s.section === sectionId ? { ...s, isCollapsed: !newIsOpen } : s,
		);
	} else {
		// Add new section
		updatedSections = [...sections, { section: sectionId, isCollapsed: !newIsOpen }];
	}

	const payload = JSON.stringify({ sections: updatedSections });
	setValue(payload);
}

export const usePersistentDevPanelCollapseState = ({
	sectionId,
	projectKey,
}: {
	sectionId: DevPanelSectionId;
	projectKey: string;
}): {
	isOpen: boolean;
	updateSectionState: (newIsOpen: boolean) => void;
	isLoading: boolean;
	getPanelCollapseState: () => void;
} => {
	const prefKey = getDevInfoPanelPrefKey(projectKey);
	const [backendState, { setValue, fetchValue }] = useUserPreferencesValue(prefKey);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Determine loading state based on data availability
	// If backendState is undefined, we're still loading (data not fetched yet)
	// If it has any value (including null), we're not loading
	const isLoading = backendState === undefined;

	// Use the backend state from the hook (which now includes the fetched value)
	// After fetchValue is called, this can be an object, but TypeScript doesn't know this
	const currentBackendState: unknown = backendState;

	// The backend state should always be a parsed object from the user preferences hook
	const parsedBackendState: BackendDevPanelState | undefined = useMemo(() => {
		if (!currentBackendState) {
			return undefined;
		}

		// If it's a string, parse it
		if (typeof currentBackendState === 'string') {
			try {
				const parsed = JSON.parse(currentBackendState);
				return isBackendDevPanelState(parsed) ? parsed : undefined;
			} catch (error) {
				return undefined;
			}
		}

		// If it's already an object, validate it
		if (typeof currentBackendState === 'object') {
			return isBackendDevPanelState(currentBackendState) ? currentBackendState : undefined;
		}

		return undefined;
	}, [currentBackendState]);

	// Get the state for this specific section from the sections array
	const backendIsOpen = useMemo(() => {
		const sectionState = parsedBackendState?.sections?.find(
			(section: BackendDevPanelSectionState) => section.section === sectionId,
		);
		// If section is collapsed, then it's not open
		return sectionState ? !sectionState.isCollapsed : false; // Default to closed
	}, [parsedBackendState, sectionId]);

	const getPanelCollapseState = useCallback(() => {
		fetchValue()
			.then((fetchedValue) => {
				// Success: send operational analytics event with the fetched value
				fireOperationalAnalytics(createAnalyticsEvent({}), 'devPanelCollapseStateFetch success', {
					sectionId,
					projectKey,
					fetchedValue: fetchedValue || null,
					isOpen: backendIsOpen,
				});
			})
			.catch((error) => {
				// Failure: send operational analytics event with current state
				fireOperationalAnalytics(createAnalyticsEvent({}), 'devPanelCollapseStateFetch failed', {
					sectionId,
					projectKey,
					error: error.message || 'Unknown error',
					isOpen: backendIsOpen,
				});
			});
	}, [fetchValue, createAnalyticsEvent, sectionId, projectKey, backendIsOpen]);

	/*
		State preference: Frontend state takes precedence over backend state.
		This ensures that frontend state changes / different entrypoint changes on frontend are respected while maintaining persistence.
	*/
	const isOpen = isLoading ? false : backendIsOpen;

	const updateSectionState = useCallback(
		(newIsOpen: boolean) => {
			updateBackendState(parsedBackendState, sectionId, newIsOpen, setValue);
		},
		[sectionId, parsedBackendState, setValue],
	);

	return {
		isOpen,
		updateSectionState,
		isLoading,
		getPanelCollapseState,
	};
};
